import React from 'react';

import { InviteDetails, InviteStatus } from '@ndvr/auth-api';
import { PersonOrgProfile } from '@ndvr/customer-api';

import { formatDateTime, getPersonName, isFutureDate } from 'utils';
import { useInvitePerson } from 'views/person/person-queries';
import { Button, ButtonSize, ButtonWithConfirmModal, ModalText, Tooltip, VerticalLayout } from 'widgets';

export const InviteButton = ({
    personOrgProfile,
    inviteDetails,
    buttonSize = 'small',
}: {
    personOrgProfile: PersonOrgProfile;
    inviteDetails: InviteDetails;
    buttonSize?: ButtonSize;
}) => {
    const invitePersonMutation = useInvitePerson();

    if (!personOrgProfile.personOrg.primaryEmail?.plainText) {
        return (
            <Tooltip tooltip="Missing email address">
                <Button size={buttonSize} disabled>
                    Invite
                </Button>
            </Tooltip>
        );
    }

    return (
        <ButtonWithConfirmModal
            buttonProps={{
                size: buttonSize,
                buttonType: 'secondary',
                children: `${inviteDetails.inviteStatus === InviteStatus.NOT_INVITED ? 'Invite' : 'Reinvite'} person`,
                waiting: invitePersonMutation.isPending,
            }}
            onConfirm={() => invitePersonMutation.mutate(personOrgProfile.personOrgGUID)}
            modalTitle={`${
                inviteDetails.inviteStatus === InviteStatus.NOT_INVITED ? 'Send' : 'Resend'
            } invite to ${getPersonName(personOrgProfile.personOrg)}`}
        >
            <VerticalLayout $gap={32}>
                <VerticalLayout $gap={4}>
                    <ModalText>
                        Are you sure you want to send a
                        {inviteDetails.inviteStatus === InviteStatus.NOT_INVITED ? 'n invite' : ' reinvite'} email to{' '}
                        <b>{personOrgProfile.personOrg.primaryEmail?.plainText}</b>?
                    </ModalText>

                    {inviteDetails.inviteTokenExpiresAt && (
                        <ModalText>
                            Last invite {isFutureDate(inviteDetails.inviteTokenExpiresAt) ? 'expires' : 'expired'} on{' '}
                            <b>{formatDateTime(inviteDetails.inviteTokenExpiresAt)}</b>
                        </ModalText>
                    )}
                </VerticalLayout>

                <VerticalLayout $gap={4}>
                    <ModalText>
                        This will use the existing household and will only create login credentials once the invite is
                        accepted.
                    </ModalText>
                </VerticalLayout>
            </VerticalLayout>
        </ButtonWithConfirmModal>
    );
};
